/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AdminButton } from '@/components/admin/button';
import { CmsSection, CmsToolbar } from "@/components/admin/cms/cms-kit";
import { AdminIcon } from '@/components/admin/icon';
import { Card, CardBody } from '@/components/admin/card';
import { useToast } from '@/components/ui/feedback';
import { extractErrorMessage } from '@/hooks/use-auth-api';
import { env } from '@/config/env';
import { siteApi } from '@/lib/site/site-api';

const inp: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--a-border)',
  borderRadius: 6,
  fontSize: 13,
  background: 'var(--a-bg)',
  color: 'var(--a-ink)',
  outline: 'none',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'block', flex: 1, minWidth: 0 }}>
      <span
        style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--a-ink-2)',
          marginBottom: 4,
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

function RowShell({ children, onRemove }: { children: React.ReactNode; onRemove: () => void }) {
  return (
    <div
      style={{
        border: '1px solid var(--a-border)',
        borderRadius: 8,
        padding: 12,
        marginBottom: 10,
        position: 'relative',
      }}
    >
      <button
        type="button"
        onClick={onRemove}
        aria-label="Supprimer"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'var(--a-danger)',
          cursor: 'pointer',
          background: 'none',
          border: 'none',
        }}
      >
        <AdminIcon n="close" s={14} />
      </button>
      {children}
    </div>
  );
}

type Language = 'fr' | 'en';
type PageKey = 'about' | 'faq' | 'made-in-cameroon' | 'cgv';

const PAGE_LABELS: Record<PageKey, string> = {
  about: 'À propos',
  faq: 'Aide & FAQ',
  'made-in-cameroon': 'Made in Cameroon',
  cgv: 'CGV (Conditions Générales de Vente)',
};

const PAGE_TABS: Array<{ key: PageKey; label: string; icon: string }> = [
  { key: 'about', label: 'À propos', icon: 'heart' },
  { key: 'faq', label: 'Aide & FAQ', icon: 'shield' },
  { key: 'made-in-cameroon', label: 'Made in Cameroon', icon: 'pin' },
  { key: 'cgv', label: 'CGV (Conditions Générales de Vente)', icon: 'cart' },
];

/**
 * Compte les textes présents en français et absents dans l'autre langue.
 *
 * Parcours récursif : les contenus éditoriaux imbriquent listes et objets sur
 * plusieurs niveaux, et c'est justement dans ces profondeurs qu'un champ oublié
 * passe inaperçu jusqu'à ce qu'un visiteur tombe sur un blanc.
 */
function countMissingTranslations(fr: unknown, en: unknown): number {
  if (typeof fr === 'string') {
    return fr.trim() && !(typeof en === 'string' && en.trim()) ? 1 : 0;
  }
  if (Array.isArray(fr)) {
    return fr.reduce<number>(
      (n, item, i) => n + countMissingTranslations(item, Array.isArray(en) ? en[i] : undefined),
      0,
    );
  }
  if (fr && typeof fr === 'object') {
    const other = (en ?? {}) as Record<string, unknown>;
    return Object.entries(fr as Record<string, unknown>).reduce<number>(
      (n, [k, v]) => n + countMissingTranslations(v, other[k]),
      0,
    );
  }
  return 0;
}

export function CmsInfoPagesEditor() {
  const qc = useQueryClient();
  const toast = useToast();

  const [activePage, setActivePage] = useState<PageKey>('about');
  const [activeLang, setActiveLang] = useState<Language>('fr');

  const { data, isFetching } = useQuery({
    queryKey: ['site-page', activePage],
    queryFn: () => siteApi.getPage(activePage),
    staleTime: 0,
  });

  const [pageData, setPageData] = useState<Record<Language, any>>({ fr: {}, en: {} });

  useEffect(() => {
    if (!data) return;
    setPageData({
      fr: data.fr ?? {},
      en: data.en ?? {},
    });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => siteApi.updatePage(activePage, pageData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['site-page', activePage] });
      toast.success(`Page « ${PAGE_LABELS[activePage]} » mise à jour avec succès.`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, 'Impossible de sauvegarder la page')),
  });

  const resetMutation = useMutation({
    mutationFn: () => siteApi.resetPage(activePage),
    onSuccess: (res) => {
      setPageData({ fr: res.fr ?? {}, en: res.en ?? {} });
      qc.invalidateQueries({ queryKey: ['site-page', activePage] });
      toast.success(`Page « ${PAGE_LABELS[activePage]} » réinitialisée.`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, 'Impossible de réinitialiser la page')),
  });

  // Helpers pour mettre à jour la sous-structure de la langue active
  const currentLangContent = pageData[activeLang] ?? {};

  const updateLangContent = (patch: Record<string, any>) => {
    setPageData((prev) => ({
      ...prev,
      [activeLang]: {
        ...prev[activeLang],
        ...patch,
      },
    }));
  };

  const getItemKey = (item: any, prefix: string, index: number): string => {
    if (item?.id) return String(item.id);
    if (item?.key) return String(item.key);
    const val = item?.title || item?.name || item?.question || item?.year || item?.n || item?.l || item?.value;
    if (val) return `${prefix}-${val}-${index}`;
    return `${prefix}-${index}`;
  };

  const updateList = <T,>(key: string, list: T[], index: number, patch: Partial<T>) => {
    const updated = list.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    updateLangContent({ [key]: updated });
  };

  const addToList = <T,>(key: string, list: T[], newItem: T) => {
    const itemWithId = {
      id: (newItem as any)?.id ?? (newItem as any)?.key ?? `id-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      ...(newItem as object),
    } as T;
    updateLangContent({ [key]: [...(list ?? []), itemWithId] });
  };

  const removeFromList = <T,>(key: string, list: T[], index: number) => {
    updateLangContent({ [key]: (list ?? []).filter((_, idx) => idx !== index) });
  };

  // Champs renseignés en français mais vides dans l'autre langue. Publier une
  // page à moitié traduite laisse des blancs au visiteur anglophone sans que
  // rien ne l'ait signalé au moment de l'enregistrement.
  const champsManquants = countMissingTranslations(pageData.fr, pageData.en);

  return (
    <Card>
      <CardBody>
        <CmsToolbar
          title={`Page « ${PAGE_LABELS[activePage]} »`}
          subtitle="Publication immédiate : l'enregistrement met le site à jour aussitôt."
          saving={saveMutation.isPending || isFetching}
          onSave={() => saveMutation.mutate()}
          saveLabel="Enregistrer la page"
          extra={
            <>
              <AdminButton
                variant="ghost"
                onClick={() => {
                  const routes: Record<PageKey, string> = {
                    about: 'a-propos',
                    faq: 'aide',
                    'made-in-cameroon': 'made-in-cameroon',
                    cgv: 'cgv',
                  };
                  window.open(`${env.storefrontUrl}/${activeLang}/${routes[activePage]}`, '_blank');
                }}
              >
                <AdminIcon n="arrowUR" s={13} /> Voir la page
              </AdminButton>
              <AdminButton
                variant="ghost"
                onClick={() => {
                  if (
                    confirm(
                      `Réinitialiser la page « ${PAGE_LABELS[activePage]} » aux valeurs par défaut ?`,
                    )
                  ) {
                    resetMutation.mutate();
                  }
                }}
                disabled={resetMutation.isPending || isFetching}
              >
                <AdminIcon n="refresh" s={13} /> Réinitialiser
              </AdminButton>
            </>
          }
        />
        {/* Barre d'onglets de navigation des 4 pages d'information */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            marginBottom: 24,
            paddingBottom: 16,
            borderBottom: '1px solid var(--a-border)',
          }}
        >
          {/* Onglets des 4 pages sous forme de Segmented Pill Bar */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'var(--a-surface-2, #f2eee3)',
              padding: '4px 6px',
              borderRadius: 12,
              border: '1px solid var(--a-border, #e5dfd0)',
              overflowX: 'auto',
              maxWidth: '100%',
            }}
          >
            {PAGE_TABS.map((pt) => {
              const isActive = activePage === pt.key;
              return (
                <button
                  key={pt.key}
                  type="button"
                  onClick={() => setActivePage(pt.key)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: 8,
                    color: isActive ? '#ffffff' : 'var(--a-ink-soft, #5c544a)',
                    background: isActive ? 'var(--a-terracotta, #c6563a)' : 'transparent',
                    boxShadow: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <AdminIcon n={pt.icon as any} s={15} />
                  <span>{pt.label}</span>
                </button>
              );
            })}
          </div>

          {/* Onglets de Langue FR / EN */}
          <div
            style={{
              display: 'flex',
              background: 'var(--a-surface-2, #f2eee3)',
              borderRadius: 8,
              padding: 3,
              border: '1px solid var(--a-border, #e5dfd0)',
            }}
          >
            <button
              type="button"
              onClick={() => setActiveLang('fr')}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                background: activeLang === 'fr' ? 'var(--a-surface, #ffffff)' : 'transparent',
                color: activeLang === 'fr' ? 'var(--a-ink, #14130f)' : 'var(--a-ink-soft)',
                border: 'none',
                cursor: 'pointer',
                boxShadow: activeLang === 'fr' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              🇫🇷 FR
            </button>
            <button
              type="button"
              onClick={() => setActiveLang('en')}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                background: activeLang === 'en' ? 'var(--a-surface, #ffffff)' : 'transparent',
                color: activeLang === 'en' ? 'var(--a-ink, #14130f)' : 'var(--a-ink-soft)',
                border: 'none',
                cursor: 'pointer',
                boxShadow: activeLang === 'en' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              🇬🇧 EN
              {/* Le nombre de textes à traduire, visible AVANT de basculer :
                  autrement, on ne découvre les blancs qu'en ouvrant la page
                  anglaise, c'est-à-dire presque jamais. */}
              {champsManquants > 0 && (
                <span
                  data-testid="lang-missing"
                  title={`${champsManquants} texte(s) renseigné(s) en français mais vide(s) en anglais`}
                  style={{
                    marginLeft: 6,
                    padding: '1px 6px',
                    borderRadius: 999,
                    background: 'var(--a-danger)',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {champsManquants}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Conteneur principal avec hauteur minimale constante pour zéro saut d'écran */}
        <div style={{ minHeight: 450 }}>
          <div>
            {/* 1. EDITEUR DE LA PAGE CGV */}
            {activePage === 'cgv' && (
              <div>
                {/* SEO Meta Tags */}

                {/* En-tête Hero */}
                <CmsSection title="Bandeau d’en-tête" where="Le grand titre en haut de la page">
                  <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                    <Field label="Badge (ex: Akwaba CM · Legal)">
                      <input
                        style={inp}
                        value={currentLangContent.heroBadge ?? ''}
                        onChange={(e) => updateLangContent({ heroBadge: e.target.value })}
                      />
                    </Field>
                    <Field label="Titre principal">
                      <input
                        style={inp}
                        value={currentLangContent.heroTitle ?? ''}
                        onChange={(e) => updateLangContent({ heroTitle: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--a-ink-mute)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginTop: 6,
                      padding: '6px 10px',
                      background: 'var(--a-bg)',
                      borderRadius: 6,
                      border: '1px dashed var(--a-border)',
                    }}
                  >
                    <AdminIcon n="clock" s={13} />
                    <span>
                      Date de mise à jour sur le site :{' '}
                      <strong>
                        {(pageData as any)?.updatedAt
                          ? new Date((pageData as any).updatedAt as string).toLocaleDateString(
                              'fr-FR',
                              {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              },
                            )
                          : 'Automatique dès enregistrement'}
                      </strong>{' '}
                      (générée automatiquement à chaque sauvegarde)
                    </span>
                  </div>
                </CmsSection>

                {/* Sections contractuelles */}
                <CmsSection title="Articles des conditions" where="Le corps du contrat, article par article">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        addToList('sections', currentLangContent.sections ?? [], {
                          id: `sec-${Date.now()}`,
                          title: 'Nouvelle section',
                          content: '',
                        })
                      }
                    >
                      <AdminIcon n="plus" s={13} /> Ajouter une section
                    </AdminButton>
                  </div>

                  {(currentLangContent.sections ?? []).map((sec: any, i: number) => (
                    <RowShell
                      key={getItemKey(sec, 'sec', i)}
                      onRemove={() =>
                        removeFromList('sections', currentLangContent.sections ?? [], i)
                      }
                    >
                      <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                        <Field label="ID Ancre (ex: livraison)">
                          <input
                            style={inp}
                            value={sec.id ?? ''}
                            onChange={(e) =>
                              updateList('sections', currentLangContent.sections ?? [], i, {
                                id: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label="Titre de la section">
                          <input
                            style={inp}
                            value={sec.title ?? ''}
                            onChange={(e) =>
                              updateList('sections', currentLangContent.sections ?? [], i, {
                                title: e.target.value,
                              })
                            }
                          />
                        </Field>
                      </div>
                      <Field label="Contenu du texte">
                        <textarea
                          style={{ ...inp, minHeight: 90 }}
                          value={sec.content ?? ''}
                          onChange={(e) =>
                            updateList('sections', currentLangContent.sections ?? [], i, {
                              content: e.target.value,
                            })
                          }
                        />
                      </Field>
                    </RowShell>
                  ))}
                </CmsSection>

                {/* Bannière Support / Aide */}
                <CmsSection title="Encart d’aide" where="Bloc de contact en bas de page">
                  <div style={{ marginBottom: 10 }}>
                    <Field label="Titre d'invitation">
                      <input
                        style={inp}
                        value={currentLangContent.supportTitle ?? ''}
                        onChange={(e) => updateLangContent({ supportTitle: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <Field label="Texte explicatif">
                      <textarea
                        style={{ ...inp, minHeight: 48 }}
                        value={currentLangContent.supportBody ?? ''}
                        onChange={(e) => updateLangContent({ supportBody: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <Field label="Libellé du bouton">
                      <input
                        style={inp}
                        value={currentLangContent.supportBtnText ?? ''}
                        onChange={(e) => updateLangContent({ supportBtnText: e.target.value })}
                      />
                    </Field>
                    <Field label="Lien d'action (ex: /aide)">
                      <input
                        style={inp}
                        value={currentLangContent.supportBtnHref ?? ''}
                        onChange={(e) => updateLangContent({ supportBtnHref: e.target.value })}
                      />
                    </Field>
                  </div>
                </CmsSection>
              </div>
            )}

            {/* 2. EDITEUR DE LA PAGE A PROPOS */}
            {activePage === 'about' && (
              <div>
                {/* SEO Meta Tags */}

                {/* Hero Header */}
                <CmsSection title="Bandeau d’en-tête" where="Le grand titre et le texte d’ouverture">
                  <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                    <Field label="Sur-titre (Eyebrow)">
                      <input
                        style={inp}
                        value={currentLangContent.heroEyebrow ?? ''}
                        onChange={(e) => updateLangContent({ heroEyebrow: e.target.value })}
                      />
                    </Field>
                    <Field label="Titre principal (Lead)">
                      <input
                        style={inp}
                        value={currentLangContent.heroLead ?? ''}
                        onChange={(e) => updateLangContent({ heroLead: e.target.value })}
                      />
                    </Field>
                    <Field label="Mot mis en valeur (Script)">
                      <input
                        style={inp}
                        value={currentLangContent.heroEm ?? ''}
                        onChange={(e) => updateLangContent({ heroEm: e.target.value })}
                      />
                    </Field>
                  </div>
                  <Field label="Texte de présentation / Histoire">
                    <textarea
                      style={{ ...inp, minHeight: 60 }}
                      value={currentLangContent.heroBody ?? ''}
                      onChange={(e) => updateLangContent({ heroBody: e.target.value })}
                    />
                  </Field>
                </CmsSection>

                {/* Statistiques */}
                <CmsSection title="Chiffres clés" where="Bandeau de chiffres qui installent la crédibilité">
                  <div
                    style={{
                      border: '1px solid var(--a-border)',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 12,
                      background: 'var(--a-bg-subtle)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                      <Field label="Titre principal">
                        <input
                          style={inp}
                          value={currentLangContent.statsLead ?? ''}
                          onChange={(e) => updateLangContent({ statsLead: e.target.value })}
                        />
                      </Field>
                      <Field label="Mot en valeur">
                        <input
                          style={inp}
                          value={currentLangContent.statsEm ?? ''}
                          onChange={(e) => updateLangContent({ statsEm: e.target.value })}
                        />
                      </Field>
                    </div>
                    <Field label="Sous-titre / Description">
                      <input
                        style={inp}
                        value={currentLangContent.statsSub ?? ''}
                        onChange={(e) => updateLangContent({ statsSub: e.target.value })}
                      />
                    </Field>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                      Chiffres clés & Statistiques
                    </h4>
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        addToList('stats', currentLangContent.stats ?? [], {
                          n: '100+',
                          l: 'nouveau chiffre',
                        })
                      }
                    >
                      <AdminIcon n="plus" s={13} /> Ajouter
                    </AdminButton>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {(currentLangContent.stats ?? []).map((s: any, i: number) => (
                      <RowShell
                        key={getItemKey(s, 'stat', i)}
                        onRemove={() => removeFromList('stats', currentLangContent.stats ?? [], i)}
                      >
                        <div style={{ marginBottom: 6 }}>
                          <Field label="Valeur (ex: 340+)">
                            <input
                              style={inp}
                              value={s.n ?? ''}
                              onChange={(e) =>
                                updateList('stats', currentLangContent.stats ?? [], i, {
                                  n: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <Field label="Libellé">
                          <input
                            style={inp}
                            value={s.l ?? ''}
                            onChange={(e) =>
                              updateList('stats', currentLangContent.stats ?? [], i, {
                                l: e.target.value,
                              })
                            }
                          />
                        </Field>
                      </RowShell>
                    ))}
                  </div>
                </CmsSection>

                {/* Timeline */}
                <CmsSection title="Notre histoire" where="Les étapes marquantes, par année">
                  <div
                    style={{
                      border: '1px solid var(--a-border)',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 12,
                      background: 'var(--a-bg-subtle)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                      <Field label="Titre principal">
                        <input
                          style={inp}
                          value={currentLangContent.timelineLead ?? ''}
                          onChange={(e) => updateLangContent({ timelineLead: e.target.value })}
                        />
                      </Field>
                      <Field label="Mot en valeur">
                        <input
                          style={inp}
                          value={currentLangContent.timelineEm ?? ''}
                          onChange={(e) => updateLangContent({ timelineEm: e.target.value })}
                        />
                      </Field>
                    </div>
                    <Field label="Sous-titre / Description">
                      <input
                        style={inp}
                        value={currentLangContent.timelineSub ?? ''}
                        onChange={(e) => updateLangContent({ timelineSub: e.target.value })}
                      />
                    </Field>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                      Étapes de l'historique
                    </h4>
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        addToList('timeline', currentLangContent.timeline ?? [], {
                          year: 2026,
                          title: 'Étape',
                          desc: 'Description',
                        })
                      }
                    >
                      <AdminIcon n="plus" s={13} /> Ajouter une étape
                    </AdminButton>
                  </div>
                  {(currentLangContent.timeline ?? []).map((t: any, i: number) => (
                    <RowShell
                      key={getItemKey(t, 'step', i)}
                      onRemove={() =>
                        removeFromList('timeline', currentLangContent.timeline ?? [], i)
                      }
                    >
                      <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                        <Field label="Année">
                          <input
                            type="number"
                            style={inp}
                            value={t.year ?? 2026}
                            onChange={(e) =>
                              updateList('timeline', currentLangContent.timeline ?? [], i, {
                                year: Number(e.target.value),
                              })
                            }
                          />
                        </Field>
                        <Field label="Titre de l'étape">
                          <input
                            style={inp}
                            value={t.title ?? ''}
                            onChange={(e) =>
                              updateList('timeline', currentLangContent.timeline ?? [], i, {
                                title: e.target.value,
                              })
                            }
                          />
                        </Field>
                      </div>
                      <Field label="Description">
                        <textarea
                          style={{ ...inp, minHeight: 48 }}
                          value={t.desc ?? ''}
                          onChange={(e) =>
                            updateList('timeline', currentLangContent.timeline ?? [], i, {
                              desc: e.target.value,
                            })
                          }
                        />
                      </Field>
                    </RowShell>
                  ))}
                </CmsSection>

                {/* Valeurs */}
                <CmsSection title="Nos valeurs" where="Ce qui distingue la marque">
                  <div
                    style={{
                      border: '1px solid var(--a-border)',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 12,
                      background: 'var(--a-bg-subtle)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                      <Field label="Titre principal">
                        <input
                          style={inp}
                          value={currentLangContent.valuesLead ?? ''}
                          onChange={(e) => updateLangContent({ valuesLead: e.target.value })}
                        />
                      </Field>
                      <Field label="Mot en valeur">
                        <input
                          style={inp}
                          value={currentLangContent.valuesEm ?? ''}
                          onChange={(e) => updateLangContent({ valuesEm: e.target.value })}
                        />
                      </Field>
                    </div>
                    <Field label="Sous-titre / Description">
                      <input
                        style={inp}
                        value={currentLangContent.valuesSub ?? ''}
                        onChange={(e) => updateLangContent({ valuesSub: e.target.value })}
                      />
                    </Field>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Liste des Valeurs</h4>
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        addToList('values', currentLangContent.values ?? [], {
                          num: '07',
                          title: 'Titre',
                          desc: 'Description',
                        })
                      }
                    >
                      <AdminIcon n="plus" s={13} /> Ajouter une valeur
                    </AdminButton>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {(currentLangContent.values ?? []).map((v: any, i: number) => (
                      <RowShell
                        key={getItemKey(v, 'val', i)}
                        onRemove={() =>
                          removeFromList('values', currentLangContent.values ?? [], i)
                        }
                      >
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                          <Field label="N° (ex: 01)">
                            <input
                              style={inp}
                              value={v.num ?? ''}
                              onChange={(e) =>
                                updateList('values', currentLangContent.values ?? [], i, {
                                  num: e.target.value,
                                })
                              }
                            />
                          </Field>
                          <Field label="Titre">
                            <input
                              style={inp}
                              value={v.title ?? ''}
                              onChange={(e) =>
                                updateList('values', currentLangContent.values ?? [], i, {
                                  title: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <Field label="Description">
                          <textarea
                            style={{ ...inp, minHeight: 48 }}
                            value={v.desc ?? ''}
                            onChange={(e) =>
                              updateList('values', currentLangContent.values ?? [], i, {
                                desc: e.target.value,
                              })
                            }
                          />
                        </Field>
                      </RowShell>
                    ))}
                  </div>
                </CmsSection>

                {/* Équipe Akwaba */}
                <CmsSection title="L’équipe" where="Les personnes derrière Akwaba">
                  <div
                    style={{
                      border: '1px solid var(--a-border)',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 12,
                      background: 'var(--a-bg-subtle)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                      <Field label="Titre principal">
                        <input
                          style={inp}
                          value={currentLangContent.teamLead ?? ''}
                          onChange={(e) => updateLangContent({ teamLead: e.target.value })}
                        />
                      </Field>
                      <Field label="Mot en valeur">
                        <input
                          style={inp}
                          value={currentLangContent.teamEm ?? ''}
                          onChange={(e) => updateLangContent({ teamEm: e.target.value })}
                        />
                      </Field>
                    </div>
                    <Field label="Sous-titre / Description">
                      <input
                        style={inp}
                        value={currentLangContent.teamSub ?? ''}
                        onChange={(e) => updateLangContent({ teamSub: e.target.value })}
                      />
                    </Field>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                      Membres de l’équipe Akwaba
                    </h4>
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        addToList('team', currentLangContent.team ?? [], {
                          name: 'Nom Prénom',
                          role: 'Rôle / Poste',
                          initials: 'NP',
                          color: '#C6563A',
                        })
                      }
                    >
                      <AdminIcon n="plus" s={13} /> Ajouter un membre
                    </AdminButton>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {(currentLangContent.team ?? []).map((m: any, i: number) => (
                      <RowShell
                        key={getItemKey(m, 'member', i)}
                        onRemove={() => removeFromList('team', currentLangContent.team ?? [], i)}
                      >
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                          <Field label="Nom complet">
                            <input
                              style={inp}
                              value={m.name ?? ''}
                              onChange={(e) =>
                                updateList('team', currentLangContent.team ?? [], i, {
                                  name: e.target.value,
                                })
                              }
                            />
                          </Field>
                          <Field label="Initiales">
                            <input
                              style={inp}
                              value={m.initials ?? ''}
                              onChange={(e) =>
                                updateList('team', currentLangContent.team ?? [], i, {
                                  initials: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Field label="Rôle / Poste">
                            <input
                              style={inp}
                              value={m.role ?? ''}
                              onChange={(e) =>
                                updateList('team', currentLangContent.team ?? [], i, {
                                  role: e.target.value,
                                })
                              }
                            />
                          </Field>
                          <Field label="Couleur avatar">
                            <input
                              style={{ ...inp, padding: 4, height: 34 }}
                              type="color"
                              value={m.color ?? '#C6563A'}
                              onChange={(e) =>
                                updateList('team', currentLangContent.team ?? [], i, {
                                  color: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                      </RowShell>
                    ))}
                  </div>
                </CmsSection>

                {/* Bannière Call To Action */}
                <CmsSection title="Encart d’appel" where="Dernier bloc de la page, avant le pied de page">
                  <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                    <Field label="Titre principal">
                      <input
                        style={inp}
                        value={currentLangContent.ctaTitle ?? ''}
                        onChange={(e) => updateLangContent({ ctaTitle: e.target.value })}
                      />
                    </Field>
                    <Field label="Mot en valeur (Script)">
                      <input
                        style={inp}
                        value={currentLangContent.ctaEm ?? ''}
                        onChange={(e) => updateLangContent({ ctaEm: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <Field label="Texte d'invitation">
                      <textarea
                        style={{ ...inp, minHeight: 48 }}
                        value={currentLangContent.ctaBody ?? ''}
                        onChange={(e) => updateLangContent({ ctaBody: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                    <Field label="Bouton 1 - Libellé">
                      <input
                        style={inp}
                        value={currentLangContent.ctaBtn1Label ?? ''}
                        onChange={(e) => updateLangContent({ ctaBtn1Label: e.target.value })}
                      />
                    </Field>
                    <Field label="Bouton 1 - Lien (Href)">
                      <input
                        style={inp}
                        value={currentLangContent.ctaBtn1Href ?? ''}
                        onChange={(e) => updateLangContent({ ctaBtn1Href: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <Field label="Bouton 2 - Libellé">
                      <input
                        style={inp}
                        value={currentLangContent.ctaBtn2Label ?? ''}
                        onChange={(e) => updateLangContent({ ctaBtn2Label: e.target.value })}
                      />
                    </Field>
                    <Field label="Bouton 2 - Lien (Href)">
                      <input
                        style={inp}
                        value={currentLangContent.ctaBtn2Href ?? ''}
                        onChange={(e) => updateLangContent({ ctaBtn2Href: e.target.value })}
                      />
                    </Field>
                  </div>
                </CmsSection>
              </div>
            )}

            {/* 3. EDITEUR DE LA PAGE AIDE / FAQ */}
            {activePage === 'faq' && (
              <div>
                {/* SEO Meta Tags */}

                {/* En-tête Hero & Recherche */}
                <CmsSection title="Bandeau d’en-tête" where="Titre et champ de recherche de l’aide">
                  <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                    <Field label="Titre principal (Lead)">
                      <input
                        style={inp}
                        value={currentLangContent.heroLead ?? ''}
                        onChange={(e) => updateLangContent({ heroLead: e.target.value })}
                      />
                    </Field>
                    <Field label="Mot en valeur (Script)">
                      <input
                        style={inp}
                        value={currentLangContent.heroEm ?? ''}
                        onChange={(e) => updateLangContent({ heroEm: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <Field label="Sous-titre / Description du Hero">
                      <input
                        style={inp}
                        value={currentLangContent.heroSub ?? ''}
                        onChange={(e) => updateLangContent({ heroSub: e.target.value })}
                      />
                    </Field>
                  </div>
                  <Field label="Placeholder du champ de recherche">
                    <input
                      style={inp}
                      value={currentLangContent.searchPlaceholder ?? ''}
                      onChange={(e) => updateLangContent({ searchPlaceholder: e.target.value })}
                    />
                  </Field>
                </CmsSection>

                {/* Raccourcis rapides */}
                <CmsSection title="Raccourcis d’aide" where="Les quatre accès rapides, en haut de l’aide">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        addToList('quick', currentLangContent.quick ?? [], {
                          icon: 'truck',
                          t: 'Titre',
                          d: 'Description',
                        })
                      }
                    >
                      <AdminIcon n="plus" s={13} /> Ajouter
                    </AdminButton>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {(currentLangContent.quick ?? []).map((q: any, i: number) => (
                      <RowShell
                        key={getItemKey(q, 'quick', i)}
                        onRemove={() => removeFromList('quick', currentLangContent.quick ?? [], i)}
                      >
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                          <Field label="Icône">
                            <select
                              style={inp}
                              value={q.icon ?? 'truck'}
                              onChange={(e) =>
                                updateList('quick', currentLangContent.quick ?? [], i, {
                                  icon: e.target.value,
                                })
                              }
                            >
                              {['truck', 'refresh', 'pay', 'shield', 'cart', 'heart'].map((ic) => (
                                <option key={ic} value={ic}>
                                  {ic}
                                </option>
                              ))}
                            </select>
                          </Field>
                          <Field label="Titre">
                            <input
                              style={inp}
                              value={q.t ?? ''}
                              onChange={(e) =>
                                updateList('quick', currentLangContent.quick ?? [], i, {
                                  t: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <Field label="Description">
                          <input
                            style={inp}
                            value={q.d ?? ''}
                            onChange={(e) =>
                              updateList('quick', currentLangContent.quick ?? [], i, {
                                d: e.target.value,
                              })
                            }
                          />
                        </Field>
                      </RowShell>
                    ))}
                  </div>
                </CmsSection>

                {/* FAQ par catégorie & Gestion des catégories */}
                <CmsSection title="Questions fréquentes" where="Les questions, groupées par thème">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <AdminButton
                      variant="ghost"
                      onClick={() => {
                        const newId = `cat_${Date.now()}`;
                        addToList('categories', currentLangContent.categories ?? [], {
                          id: newId,
                          label: 'Nouvelle catégorie',
                        });
                      }}
                    >
                      <AdminIcon n="plus" s={13} /> Ajouter une catégorie
                    </AdminButton>
                  </div>

                  {(currentLangContent.categories ?? []).map((cat: any, catIndex: number) => {
                    const catFaq = (currentLangContent.faq ?? {})[cat.id] ?? [];
                    return (
                      <div
                        key={getItemKey(cat, 'cat', catIndex)}
                        style={{
                          border: '1px solid var(--a-border)',
                          borderRadius: 8,
                          padding: 14,
                          marginBottom: 16,
                          background: 'var(--a-bg-subtle)',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: 12,
                            borderBottom: '1px solid var(--a-border)',
                            paddingBottom: 10,
                          }}
                        >
                          <div style={{ flex: 1, marginRight: 12 }}>
                            <div style={{ display: 'flex', gap: 10 }}>
                              <Field label="Titre de la catégorie">
                                <input
                                  style={{ ...inp, fontWeight: 700 }}
                                  value={cat.label ?? ''}
                                  onChange={(e) =>
                                    updateList(
                                      'categories',
                                      currentLangContent.categories ?? [],
                                      catIndex,
                                      { label: e.target.value },
                                    )
                                  }
                                />
                              </Field>
                              <Field label="Identifiant (ID unique)">
                                <input
                                  style={{ ...inp, fontFamily: 'monospace' }}
                                  value={cat.id ?? ''}
                                  onChange={(e) => {
                                    const oldId = cat.id;
                                    const newId = e.target.value;
                                    const existingFaq = currentLangContent.faq ?? {};
                                    const categoryFaqData = existingFaq[oldId] ?? [];
                                    const updatedFaq = { ...existingFaq };
                                    delete updatedFaq[oldId];
                                    updatedFaq[newId] = categoryFaqData;
                                    updateList(
                                      'categories',
                                      currentLangContent.categories ?? [],
                                      catIndex,
                                      { id: newId },
                                    );
                                    updateLangContent({ faq: updatedFaq });
                                  }}
                                />
                              </Field>
                            </div>
                          </div>
                          <AdminButton
                            variant="ghost"
                            onClick={() =>
                              removeFromList(
                                'categories',
                                currentLangContent.categories ?? [],
                                catIndex,
                              )
                            }
                            style={{ color: 'var(--a-accent)', marginTop: 20 }}
                          >
                            <AdminIcon n="close" s={13} /> Supprimer catégorie
                          </AdminButton>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 8,
                          }}
                        >
                          <span
                            style={{ fontSize: 12, fontWeight: 700, color: 'var(--a-ink-mute)' }}
                          >
                            Questions dans {cat.label} ({catFaq.length})
                          </span>
                          <AdminButton
                            variant="ghost"
                            onClick={() => {
                              const newFaq = [
                                ...catFaq,
                                { q: 'Nouvelle question ?', a: 'Réponse ici.' },
                              ];
                              updateLangContent({
                                faq: {
                                  ...(currentLangContent.faq ?? {}),
                                  [cat.id]: newFaq,
                                },
                              });
                            }}
                          >
                            <AdminIcon n="plus" s={12} /> Ajouter une question
                          </AdminButton>
                        </div>

                        {catFaq.map((item: any, i: number) => (
                          <RowShell
                            key={getItemKey(item, 'faq', i)}
                            onRemove={() => {
                              const newFaq = catFaq.filter((_: any, idx: number) => idx !== i);
                              updateLangContent({
                                faq: {
                                  ...(currentLangContent.faq ?? {}),
                                  [cat.id]: newFaq,
                                },
                              });
                            }}
                          >
                            <div style={{ marginBottom: 6 }}>
                              <Field label="Question">
                                <input
                                  style={inp}
                                  value={item.q ?? ''}
                                  onChange={(e) => {
                                    const newFaq = catFaq.map((qa: any, idx: number) =>
                                      idx === i ? { ...qa, q: e.target.value } : qa,
                                    );
                                    updateLangContent({
                                      faq: { ...(currentLangContent.faq ?? {}), [cat.id]: newFaq },
                                    });
                                  }}
                                />
                              </Field>
                            </div>
                            <Field label="Réponse">
                              <textarea
                                style={{ ...inp, minHeight: 48 }}
                                value={item.a ?? ''}
                                onChange={(e) => {
                                  const newFaq = catFaq.map((qa: any, idx: number) =>
                                    idx === i ? { ...qa, a: e.target.value } : qa,
                                  );
                                  updateLangContent({
                                    faq: { ...(currentLangContent.faq ?? {}), [cat.id]: newFaq },
                                  });
                                }}
                              />
                            </Field>
                          </RowShell>
                        ))}
                      </div>
                    );
                  })}
                </CmsSection>

                {/* Bannière de contact & Canaux */}
                <CmsSection title="Encart de contact" where="Canaux de contact en bas de page">
                  <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                    <Field label="Titre principal">
                      <input
                        style={inp}
                        value={currentLangContent.replyLead ?? ''}
                        onChange={(e) => updateLangContent({ replyLead: e.target.value })}
                      />
                    </Field>
                    <Field label="Mot en valeur (Script)">
                      <input
                        style={inp}
                        value={currentLangContent.replyEm ?? ''}
                        onChange={(e) => updateLangContent({ replyEm: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <Field label="Texte d'explication">
                      <textarea
                        style={{ ...inp, minHeight: 48 }}
                        value={currentLangContent.replyBody ?? ''}
                        onChange={(e) => updateLangContent({ replyBody: e.target.value })}
                      />
                    </Field>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 10,
                    }}
                  >
                    <h5 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>
                      Canaux de contact (WhatsApp, Téléphone, Email)
                    </h5>
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        addToList('channels', currentLangContent.channels ?? [], {
                          iconBg: '#25D366',
                          icon: 'phone2',
                          t: 'Canal · Titre',
                          d: 'Coordonnée',
                          href: 'https://...',
                        })
                      }
                    >
                      <AdminIcon n="plus" s={12} /> Ajouter un canal
                    </AdminButton>
                  </div>
                  {(currentLangContent.channels ?? []).map((ch: any, i: number) => (
                    <RowShell
                      key={getItemKey(ch, 'channel', i)}
                      onRemove={() =>
                        removeFromList('channels', currentLangContent.channels ?? [], i)
                      }
                    >
                      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                        <Field label="Titre (ex: WhatsApp · 4 min)">
                          <input
                            style={inp}
                            value={ch.t ?? ''}
                            onChange={(e) =>
                              updateList('channels', currentLangContent.channels ?? [], i, {
                                t: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label="Texte / Numéro / Email">
                          <input
                            style={inp}
                            value={ch.d ?? ''}
                            onChange={(e) =>
                              updateList('channels', currentLangContent.channels ?? [], i, {
                                d: e.target.value,
                              })
                            }
                          />
                        </Field>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Field label="Lien d'action (wa.me, tel:, mailto:)">
                          <input
                            style={inp}
                            value={ch.href ?? ''}
                            onChange={(e) =>
                              updateList('channels', currentLangContent.channels ?? [], i, {
                                href: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label="Couleur icône">
                          <input
                            style={{ ...inp, padding: 4, height: 34 }}
                            type="color"
                            value={ch.iconBg ?? '#C6563A'}
                            onChange={(e) =>
                              updateList('channels', currentLangContent.channels ?? [], i, {
                                iconBg: e.target.value,
                              })
                            }
                          />
                        </Field>
                      </div>
                    </RowShell>
                  ))}
                </CmsSection>
              </div>
            )}

            {/* 4. EDITEUR DE LA PAGE MADE IN CAMEROON */}
            {activePage === 'made-in-cameroon' && (
              <div>
                {/* SEO Meta Tags */}

                {/* En-tête Hero */}
                <CmsSection title="Bandeau d’en-tête" where="Le grand titre en haut de la page">
                  <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                    <Field label="Badge (ex: Made in Cameroon)">
                      <input
                        style={inp}
                        value={currentLangContent.heroBadge ?? ''}
                        onChange={(e) => updateLangContent({ heroBadge: e.target.value })}
                      />
                    </Field>
                    <Field label="Titre (Lead)">
                      <input
                        style={inp}
                        value={currentLangContent.heroLead ?? ''}
                        onChange={(e) => updateLangContent({ heroLead: e.target.value })}
                      />
                    </Field>
                    <Field label="Mot en valeur (Script)">
                      <input
                        style={inp}
                        value={currentLangContent.heroEm ?? ''}
                        onChange={(e) => updateLangContent({ heroEm: e.target.value })}
                      />
                    </Field>
                    <Field label="Fin de titre">
                      <input
                        style={inp}
                        value={currentLangContent.heroTail ?? ''}
                        onChange={(e) => updateLangContent({ heroTail: e.target.value })}
                      />
                    </Field>
                  </div>
                  <Field label="Texte de présentation / Histoire">
                    <textarea
                      style={{ ...inp, minHeight: 60 }}
                      value={currentLangContent.heroBody ?? ''}
                      onChange={(e) => updateLangContent({ heroBody: e.target.value })}
                    />
                  </Field>
                </CmsSection>

                {/* Hero Stats */}
                <CmsSection title="Chiffres clés" where="Les repères chiffrés du programme">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        addToList('heroStats', currentLangContent.heroStats ?? [], {
                          n: '100+',
                          l: 'stat',
                        })
                      }
                    >
                      <AdminIcon n="plus" s={13} /> Ajouter
                    </AdminButton>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {(currentLangContent.heroStats ?? []).map((s: any, i: number) => (
                      <RowShell
                        key={getItemKey(s, 'herostat', i)}
                        onRemove={() =>
                          removeFromList('heroStats', currentLangContent.heroStats ?? [], i)
                        }
                      >
                        <div style={{ marginBottom: 6 }}>
                          <Field label="Chiffre">
                            <input
                              style={inp}
                              value={s.n ?? ''}
                              onChange={(e) =>
                                updateList('heroStats', currentLangContent.heroStats ?? [], i, {
                                  n: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <Field label="Libellé">
                          <input
                            style={inp}
                            value={s.l ?? ''}
                            onChange={(e) =>
                              updateList('heroStats', currentLangContent.heroStats ?? [], i, {
                                l: e.target.value,
                              })
                            }
                          />
                        </Field>
                      </RowShell>
                    ))}
                  </div>
                </CmsSection>

                {/* Ateliers & Vendeurs Locaux */}
                <CmsSection title="Ateliers et artisans" where="Les partenaires mis en avant">
                  <div
                    style={{
                      border: '1px solid var(--a-border)',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 12,
                      background: 'var(--a-bg-subtle)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                      <Field label="Sur-titre (Eyebrow)">
                        <input
                          style={inp}
                          value={currentLangContent.atelierEyebrow ?? ''}
                          onChange={(e) => updateLangContent({ atelierEyebrow: e.target.value })}
                        />
                      </Field>
                      <Field label="Titre principal">
                        <input
                          style={inp}
                          value={currentLangContent.atelierLead ?? ''}
                          onChange={(e) => updateLangContent({ atelierLead: e.target.value })}
                        />
                      </Field>
                      <Field label="Mot en valeur">
                        <input
                          style={inp}
                          value={currentLangContent.atelierEm ?? ''}
                          onChange={(e) => updateLangContent({ atelierEm: e.target.value })}
                        />
                      </Field>
                    </div>
                    <Field label="Description / Présentation">
                      <textarea
                        style={{ ...inp, minHeight: 48 }}
                        value={currentLangContent.atelierBody ?? ''}
                        onChange={(e) => updateLangContent({ atelierBody: e.target.value })}
                      />
                    </Field>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                      Ateliers & Vendeurs à la une
                    </h4>
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        addToList('vendors', currentLangContent.vendors ?? [], {
                          name: 'Nouvel Atelier',
                          region: 'Yaoundé',
                          craft: 'Artisanat',
                          initials: 'NA',
                          color: '#C6563A',
                          quote: '« Notre histoire... »',
                          since: 'Depuis 2024',
                          slug: '',
                          hasPage: false,
                        })
                      }
                    >
                      <AdminIcon n="plus" s={13} /> Ajouter un atelier
                    </AdminButton>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {(currentLangContent.vendors ?? []).map((v: any, i: number) => (
                      <RowShell
                        key={getItemKey(v, 'vendor', i)}
                        onRemove={() =>
                          removeFromList('vendors', currentLangContent.vendors ?? [], i)
                        }
                      >
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                          <Field label="Nom de l'atelier">
                            <input
                              style={inp}
                              value={v.name ?? ''}
                              onChange={(e) =>
                                updateList('vendors', currentLangContent.vendors ?? [], i, {
                                  name: e.target.value,
                                })
                              }
                            />
                          </Field>
                          <Field label="Région / Ville">
                            <input
                              style={inp}
                              value={v.region ?? ''}
                              onChange={(e) =>
                                updateList('vendors', currentLangContent.vendors ?? [], i, {
                                  region: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                          <Field label="Savoir-faire / Métier">
                            <input
                              style={inp}
                              value={v.craft ?? ''}
                              onChange={(e) =>
                                updateList('vendors', currentLangContent.vendors ?? [], i, {
                                  craft: e.target.value,
                                })
                              }
                            />
                          </Field>
                          <Field label="Depuis (ex: Depuis 2020)">
                            <input
                              style={inp}
                              value={v.since ?? ''}
                              onChange={(e) =>
                                updateList('vendors', currentLangContent.vendors ?? [], i, {
                                  since: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                          <Field label="Initiales">
                            <input
                              style={inp}
                              value={v.initials ?? ''}
                              onChange={(e) =>
                                updateList('vendors', currentLangContent.vendors ?? [], i, {
                                  initials: e.target.value,
                                })
                              }
                            />
                          </Field>
                          <Field label="Couleur badge">
                            <input
                              style={{ ...inp, padding: 4, height: 34 }}
                              type="color"
                              value={v.color ?? '#C6563A'}
                              onChange={(e) =>
                                updateList('vendors', currentLangContent.vendors ?? [], i, {
                                  color: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <div style={{ marginBottom: 6 }}>
                          <Field label="Citation / Engagements">
                            <textarea
                              style={{ ...inp, minHeight: 40 }}
                              value={v.quote ?? ''}
                              onChange={(e) =>
                                updateList('vendors', currentLangContent.vendors ?? [], i, {
                                  quote: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Field label="Slug URL (ex: atelier-mboa)">
                            <input
                              style={inp}
                              value={v.slug ?? ''}
                              onChange={(e) =>
                                updateList('vendors', currentLangContent.vendors ?? [], i, {
                                  slug: e.target.value,
                                })
                              }
                            />
                          </Field>
                          <Field label="Lien boutique">
                            <select
                              style={inp}
                              value={v.hasPage ? 'true' : 'false'}
                              onChange={(e) =>
                                updateList('vendors', currentLangContent.vendors ?? [], i, {
                                  hasPage: e.target.value === 'true',
                                })
                              }
                            >
                              <option value="true">Actif</option>
                              <option value="false">Bientôt</option>
                            </select>
                          </Field>
                        </div>
                      </RowShell>
                    ))}
                  </div>
                </CmsSection>

                {/* Régions du Cameroun */}
                <CmsSection title="Régions couvertes" where="La carte des régions et leurs produits">
                  <div
                    style={{
                      border: '1px solid var(--a-border)',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 12,
                      background: 'var(--a-bg-subtle)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                      <Field label="Titre principal">
                        <input
                          style={inp}
                          value={currentLangContent.regionsLead ?? ''}
                          onChange={(e) => updateLangContent({ regionsLead: e.target.value })}
                        />
                      </Field>
                      <Field label="Mot en valeur">
                        <input
                          style={inp}
                          value={currentLangContent.regionsEm ?? ''}
                          onChange={(e) => updateLangContent({ regionsEm: e.target.value })}
                        />
                      </Field>
                    </div>
                    <Field label="Sous-titre / Description">
                      <input
                        style={inp}
                        value={currentLangContent.regionsSub ?? ''}
                        onChange={(e) => updateLangContent({ regionsSub: e.target.value })}
                      />
                    </Field>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                      Régions représentées
                    </h4>
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        addToList('regions', currentLangContent.regions ?? [], {
                          n: (currentLangContent.regions?.length ?? 0) + 1,
                          name: 'Région',
                          meta: 'Villes principal',
                          count: '10 vendeurs',
                        })
                      }
                    >
                      <AdminIcon n="plus" s={13} /> Ajouter une région
                    </AdminButton>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {(currentLangContent.regions ?? []).map((r: any, i: number) => (
                      <RowShell
                        key={getItemKey(r, 'region', i)}
                        onRemove={() =>
                          removeFromList('regions', currentLangContent.regions ?? [], i)
                        }
                      >
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                          <Field label="N° (ex: 1)">
                            <input
                              type="number"
                              style={inp}
                              value={r.n ?? i + 1}
                              onChange={(e) =>
                                updateList('regions', currentLangContent.regions ?? [], i, {
                                  n: Number(e.target.value),
                                })
                              }
                            />
                          </Field>
                          <Field label="Nom région">
                            <input
                              style={inp}
                              value={r.name ?? ''}
                              onChange={(e) =>
                                updateList('regions', currentLangContent.regions ?? [], i, {
                                  name: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <div style={{ marginBottom: 6 }}>
                          <Field label="Villes clés (meta)">
                            <input
                              style={inp}
                              value={r.meta ?? ''}
                              onChange={(e) =>
                                updateList('regions', currentLangContent.regions ?? [], i, {
                                  meta: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <Field label="Compteur (ex: 124 vendeurs)">
                          <input
                            style={inp}
                            value={r.count ?? ''}
                            onChange={(e) =>
                              updateList('regions', currentLangContent.regions ?? [], i, {
                                count: e.target.value,
                              })
                            }
                          />
                        </Field>
                      </RowShell>
                    ))}
                  </div>
                </CmsSection>

                {/* Piliers / Engagements */}
                <div>
                  <div
                    style={{
                      border: '1px solid var(--a-border)',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 12,
                      background: 'var(--a-bg-subtle)',
                    }}
                  >
                    <h5 style={{ margin: '0 0 8px 0', fontSize: 12, fontWeight: 700 }}>
                      En-tête Section Piliers
                    </h5>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                      <Field label="Titre principal">
                        <input
                          style={inp}
                          value={currentLangContent.pillarsLead ?? ''}
                          onChange={(e) => updateLangContent({ pillarsLead: e.target.value })}
                        />
                      </Field>
                      <Field label="Mot en valeur">
                        <input
                          style={inp}
                          value={currentLangContent.pillarsEm ?? ''}
                          onChange={(e) => updateLangContent({ pillarsEm: e.target.value })}
                        />
                      </Field>
                    </div>
                    <Field label="Sous-titre / Description">
                      <input
                        style={inp}
                        value={currentLangContent.pillarsSub ?? ''}
                        onChange={(e) => updateLangContent({ pillarsSub: e.target.value })}
                      />
                    </Field>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                      Engagements & Piliers
                    </h4>
                    <AdminButton
                      variant="ghost"
                      onClick={() =>
                        addToList('pillars', currentLangContent.pillars ?? [], {
                          icon: 'shield',
                          t: 'Pilier',
                          d: 'Description',
                        })
                      }
                    >
                      <AdminIcon n="plus" s={13} /> Ajouter
                    </AdminButton>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {(currentLangContent.pillars ?? []).map((p: any, i: number) => (
                      <RowShell
                        key={getItemKey(p, 'pillar', i)}
                        onRemove={() =>
                          removeFromList('pillars', currentLangContent.pillars ?? [], i)
                        }
                      >
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                          <Field label="Icône">
                            <select
                              style={inp}
                              value={p.icon ?? 'shield'}
                              onChange={(e) =>
                                updateList('pillars', currentLangContent.pillars ?? [], i, {
                                  icon: e.target.value,
                                })
                              }
                            >
                              {['shield', 'truck', 'pay', 'heart'].map((ic) => (
                                <option key={ic} value={ic}>
                                  {ic}
                                </option>
                              ))}
                            </select>
                          </Field>
                          <Field label="Titre">
                            <input
                              style={inp}
                              value={p.t ?? ''}
                              onChange={(e) =>
                                updateList('pillars', currentLangContent.pillars ?? [], i, {
                                  t: e.target.value,
                                })
                              }
                            />
                          </Field>
                        </div>
                        <Field label="Description">
                          <textarea
                            style={{ ...inp, minHeight: 48 }}
                            value={p.d ?? ''}
                            onChange={(e) =>
                              updateList('pillars', currentLangContent.pillars ?? [], i, {
                                d: e.target.value,
                              })
                            }
                          />
                        </Field>
                      </RowShell>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
